import { Fragment, useLayoutEffect, useRef } from 'react';
import type { Conversation, ContactDetail } from './types';
import { useThread } from '../../hooks/useThread';
import { dayKey, CHANNEL_LABEL, displayName } from './convUtils';
import ChannelIcon from './ChannelIcon';
import MessageBubble from './MessageBubble';
import Composer from './Composer';

type Ch = 'sms' | 'email';

/** Within this many pixels of the bottom counts as "at the newest" - a new message keeps him there. */
const NEAR_BOTTOM_PX = 80;

export default function MessageThread({
  conversation,
  contact,
  requestedChannel,
  autoDraftTodo,
  onBack,
  onToggleContext,
}: {
  conversation: Conversation | null;
  contact: ContactDetail | null;
  requestedChannel?: { ch: Ch; n: number } | null;
  /** TODO1: the to-do this thread was opened for - its composer drafts once for it (Composer). */
  autoDraftTodo?: string;
  onBack?: () => void;
  onToggleContext?: () => void;
}) {
  const channel = conversation?.channel || 'other';
  const { messages, loading, error, sending, send, draft, sendingNumber } = useThread(conversation?.id ?? null, channel);
  // CONV2 (b): like every SMS app - the newest at the BOTTOM, a thread OPENS there, and a new message keeps him there.
  // ⛔ Unless he has scrolled up to read: then a new message must not pull him down. (The old `scrollIntoView` on a
  // marker could also scroll the whole PAGE on a phone; this moves only the thread's own scroll box.)
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const atBottom = useRef(true);
  useLayoutEffect(() => {
    atBottom.current = true; // a newly opened thread opens at the newest
  }, [conversation?.id]);
  const newest = messages.length ? messages[messages.length - 1] : null;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || loading) return;
    // His own text just sent always shows, even if he had scrolled up.
    if (atBottom.current || newest?.status === 'pending') el.scrollTop = el.scrollHeight;
  }, [newest?.id, newest?.status, messages.length, loading, conversation?.id]);
  const onScroll = () => {
    const el = scrollRef.current;
    if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
        Select a conversation to read the thread
      </div>
    );
  }

  const name = displayName(conversation.contact_name || contact?.name || conversation.phone);

  // Channel capabilities come from the full contact when loaded, else the
  // conversation row. SMS needs a phone, Email needs an email.
  const phone = contact?.phone ?? conversation.phone ?? null;
  const email = contact?.email ?? conversation.email ?? null;
  const availableChannels: Ch[] = [];
  if (phone) availableChannels.push('sms');
  if (email) availableChannels.push('email');

  let lastDay = '';

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-gray-200">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button type="button" onClick={onBack} className="lg:hidden text-gray-400 hover:text-gray-700 text-sm" aria-label="Back">←</button>
          )}
          <span className="truncate text-sm font-semibold text-gray-900">{name}</span>
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            <ChannelIcon channel={channel} size={11} /> {CHANNEL_LABEL[channel] || 'Message'}
          </span>
        </div>
        {onToggleContext && (
          <button type="button" onClick={onToggleContext} className="xl:hidden text-xs px-2 py-1 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
            Details
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} onScroll={onScroll} data-testid="thread-scroll" className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
        {loading ? (
          <div className="text-center py-8 text-sm text-gray-400">Loading…</div>
        ) : error ? (
          <div className="text-center py-8 text-sm text-red-600">{error}</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400">No messages in this conversation</div>
        ) : (
          messages.map((m) => {
            const dk = dayKey(m.date_added);
            const showDay = dk && dk !== lastDay;
            lastDay = dk;
            return (
              <Fragment key={m.id}>
                {showDay && <div className="text-center text-[10px] text-gray-400 my-2">{dk}</div>}
                <MessageBubble m={m} />
              </Fragment>
            );
          })
        )}
      </div>

      {/* Composer */}
      <Composer
        availableChannels={availableChannels}
        defaultChannel={channel}
        recipients={{ sms: phone, email }}
        contactName={name}
        dnd={contact?.dnd ?? false}
        dndChannels={contact?.dnd_channels}
        sending={sending}
        requestedChannel={requestedChannel}
        sendingNumber={sendingNumber}
        autoDraftTodo={autoDraftTodo}
        onSend={(msg, subj, ch, fromNumber) => send(msg, subj, ch, fromNumber)}
        onDraft={(ch, todoTaskId) => draft(ch, todoTaskId)}
      />
    </div>
  );
}
